import { Stack, Text, VisuallyHidden } from "@mantine/core";
import type { DragEvent, ReactNode } from "react";

import imageGlyph from "./assets/image-card-placeholder.svg";
import videoGlyph from "./assets/video-placeholder.svg";
import { GenerationLoader } from "./GenerationLoader";
import classes from "./MediaCard.module.css";

export interface MediaCardProps {
  /** The image or video, filling the card. */
  children: ReactNode;
  /**
   * Actions in a pill at the bottom center, shown on hover; what sits at the card's bottom (a
   * video's time pill) rises above it then, by `--cai-card-lift`, as live's.
   */
  menu?: ReactNode;
  /** A generation kept in the library: the card gets the accent gradient ring. */
  saved?: boolean;
  /** Top right, shown with the hover (always on a touch screen): the Studio's "Open preview". */
  corner?: ReactNode;
  /**
   * Checked in a selection (the library's, S1c): an accent ring with a gap, a light tint, and
   * the corner shown without the hover.
   */
  selected?: boolean;
  /**
   * No rise of its own: a library card, which live's grid shows in its rising column alone, and
   * which a long list mounts again as it scrolls back into view (#784, #794).
   */
  still?: boolean;
  /** The card may be dragged (a library card onto a project tile, spec library-card-drag). */
  drag?: MediaCardDrag | undefined;
}

export interface MediaCardDrag {
  onDragStart: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}

/**
 * A media card (design-system.md 3.3, 3.5): the media clipped to 14px corners, growing 5% on
 * hover, appearing from below; the actions sit in a frosted pill at the bottom center. Live's
 * library cards show no kind badge, at rest or on hover (design-system decision 67).
 */
export function MediaCard({
  children,
  menu,
  saved = false,
  corner,
  selected = false,
  still = false,
  drag,
}: MediaCardProps) {
  return (
    <div
      className={classes.card}
      draggable={drag === undefined ? undefined : true}
      onDragStart={drag?.onDragStart}
      onDragEnd={drag?.onDragEnd}
      data-saved={saved || undefined}
      data-selected={selected || undefined}
      data-still={still || undefined}
      data-menu={menu !== undefined || undefined}
    >
      <div className={classes.media}>{children}</div>
      {menu === undefined ? null : <div className={classes.menu}>{menu}</div>}
      {corner === undefined ? null : <div className={classes.corner}>{corner}</div>}
    </div>
  );
}

/** What an output is: live draws a picture's glyph or a video's, and a running video blurred. */
export type MediaKind = "image" | "video";

/** Live's glyph for a tile with no output yet, or a failed one (spec decision 20). */
function glyphOf(medium: MediaKind): string {
  return medium === "video" ? videoGlyph : imageGlyph;
}

export interface MediaPlaceholderProps {
  /** The shape it was asked for, such as "16:9"; square when the request named none. */
  aspectRatio?: string | null;
  /** Read by a screen reader: "Output 2, waiting". */
  label: string;
  /** A picture unless said otherwise. */
  medium?: MediaKind;
}

/** A tile with no output, as a soft box at its shape with live's glyph centered. */
export function MediaPlaceholder({ aspectRatio, label, medium = "image" }: MediaPlaceholderProps) {
  const ratio = aspectRatio?.replace(":", " / ") ?? "1";
  return (
    <div className={classes.placeholder} style={{ aspectRatio: ratio }}>
      <img src={glyphOf(medium)} alt="" className={classes.glyph} />
      <VisuallyHidden>{label}</VisuallyHidden>
    </div>
  );
}

/**
 * An output being made, as live shows one: its loader (`GenerationLoader`) in the soft box at its
 * shape, over live's blurred placeholder for a video (spec ui-perf/ui-switch-classic-simple-pro,
 * decisions 19 and 20).
 */
export function MediaLoading({ aspectRatio, label, medium = "image" }: MediaPlaceholderProps) {
  const ratio = aspectRatio?.replace(":", " / ") ?? "1";
  return (
    <div className={classes.placeholder} style={{ aspectRatio: ratio }} data-loading>
      <GenerationLoader label={label} video={medium === "video"} />
    </div>
  );
}

export interface MediaFailedProps {
  title: string;
  description?: string;
  /** A picture unless said otherwise: live's failed card shows the picture's or the video's glyph. */
  medium?: MediaKind;
}

/** A generation that failed: one box across the row, live's glyph over what went wrong. */
export function MediaFailed({ title, description, medium = "image" }: MediaFailedProps) {
  return (
    <Stack align="center" justify="center" gap={8} className={classes.failed} role="alert">
      <img src={glyphOf(medium)} alt="" className={classes.glyph} />
      <Text fw={500}>{title}</Text>
      {description === undefined ? null : (
        <Text c="var(--cai-color-text-muted)">{description}</Text>
      )}
    </Stack>
  );
}
